import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import adminConsoleService from '@services/adminConsoleService';
import type {
  MenuItem,
  PersonListItem,
  ProjectListItem,
  LayerListItem,
  SecuritySubGroup,
} from '@services/adminConsoleService';

interface AddSubGroupModalProps {
  groupId: number;
  onClose: () => void;
  onSave: (created: SecuritySubGroup) => void;
}

type Step = 'name' | 'config' | 'engagements' | 'users';

const PERM_FIELDS = [
  { key: 'View', label: 'View/Reply' },
  { key: 'Edit', label: 'View/Reply/Edit' },
  { key: 'Temporary', label: 'View/Reply/Temporary Edit Reply' },
  { key: 'Delete', label: 'Delete' },
];

const CustomCheckbox: React.FC<{ checked: boolean; onChange: () => void }> = ({
  checked,
  onChange,
}) => (
  <button
    type="button"
    role="checkbox"
    aria-checked={checked}
    onClick={e =>
    {
      e.stopPropagation();
      onChange();
    }}
    className={`w-4 h-4 flex-shrink-0 border flex items-center justify-center transition-colors
      ${checked ? 'bg-white border-[#62aacb]' : 'bg-white border-[#ccc]'}`}
  >
    {checked && (
      <span className="material-icons text-[#62aacb]" style={{ fontSize: '16px' }}>
        check
      </span>
    )}
  </button>
);

export const AddSubGroupModal: React.FC<AddSubGroupModalProps> = ({ groupId, onClose, onSave }) =>
{
  const [step, setStep] = useState<Step>('name');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Step 1 — Name
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');

  // Step 2 — Permissions, Card Types, Layers
  const [perms, setPerms] = useState({ View: false, Edit: false, Temporary: false, Delete: false });
  const [menuItems, setMenuItems] = useState<MenuItem[]>([]);
  const [selectedCardTypeIds, setSelectedCardTypeIds] = useState<Set<number>>(new Set());
  const [layersList, setLayersList] = useState<LayerListItem[]>([]);
  const [selectedLayerIds, setSelectedLayerIds] = useState<Set<number>>(new Set());
  const [loadingConfig, setLoadingConfig] = useState(false);

  // Step 3 — Engagements
  const [projectsList, setProjectsList] = useState<ProjectListItem[]>([]);
  const [selectedProjectIds, setSelectedProjectIds] = useState<Set<number>>(new Set());
  const [projectSearch, setProjectSearch] = useState('');
  const [loadingProjects, setLoadingProjects] = useState(false);

  // Step 4 — Users
  const [peopleList, setPeopleList] = useState<PersonListItem[]>([]);
  const [selectedPersonIds, setSelectedPersonIds] = useState<Set<number>>(new Set());
  const [userSearch, setUserSearch] = useState('');
  const [loadingPeople, setLoadingPeople] = useState(false);

  // Load config data when entering step 2
  useEffect(() =>
  {
    if (step !== 'config' || menuItems.length > 0) return;
    setLoadingConfig(true);
    Promise.all([adminConsoleService.getMenuItems(), adminConsoleService.getLayersList()])
      .then(([items, layers]) =>
      {
        setMenuItems(items);
        setLayersList(layers);
        console.log('Loaded config data:', { items, layers });
      })
      .catch(() => setError('Failed to load configuration data.'))
      .finally(() => setLoadingConfig(false));
  }, [step]);

  // Load projects when entering step 3
  useEffect(() =>
  {
    if (step !== 'engagements' || projectsList.length > 0) return;
    setLoadingProjects(true);
    adminConsoleService
      .getAllProjects()
      .then(setProjectsList)
      .catch(() => setError('Failed to load projects.'))
      .finally(() => setLoadingProjects(false));
  }, [step]);

  // Load people when entering step 4
  useEffect(() =>
  {
    if (step !== 'users' || peopleList.length > 0) return;
    setLoadingPeople(true);
    adminConsoleService
      .getPeopleList()
      .then(setPeopleList)
      .catch(() => setError('Failed to load users.'))
      .finally(() => setLoadingPeople(false));
  }, [step]);

  const goNext = () =>
  {
    setError(null);
    if (step === 'name')
    {
      if (!name.trim())
      {
        setError('Name is required.');
        return;
      }
      setStep('config');
    }
    else if (step === 'config')
    {
      setStep('engagements');
    }
    else if (step === 'engagements')
    {
      setStep('users');
    }
  };

  const goBack = () =>
  {
    setError(null);
    if (step === 'config') setStep('name');
    else if (step === 'engagements') setStep('config');
    else if (step === 'users') setStep('engagements');
  };

  const handleSave = async () =>
  {
    setSaving(true);
    setError(null);
    try
    {
      const created = await adminConsoleService.saveSecurityGroup(groupId, {
        Name: name.trim(),
        Description: description,
        View: perms.View,
        Edit: perms.Edit,
        Temporary: perms.Temporary,
        Delete: perms.Delete,
        ItemCreation: false,
        IsBeta: false,
        IsAdminGroup: false,
        ConfigValueSecurityGroups: menuItems
          .filter(i => selectedCardTypeIds.has(i.ConfigValueID))
          .map(i => ({ ConfigValueID: i.ConfigValueID, Name: i.Text, Icon: i.Icon })),
        SecurityGroupLayers: layersList
          .filter(l => selectedLayerIds.has(l.LayerId))
          .map(l => ({ LayerId: l.LayerId, Name: l.Name, Color: l.Color, TextColor: l.TextColor })),
        SecurityGroupProjects: projectsList
          .filter(p => selectedProjectIds.has(p.ProjectID))
          .map(p => ({ Name: p.Name, ProjectID: p.ProjectID })),
        PeopleSecurityGroup: peopleList
          .filter(p => selectedPersonIds.has(p.PersonID))
          .map(p => ({ PersonID: p.PersonID, Name: p.Name, PathImage: p.PathImage })),
      });

      onSave({
        ...created,
        Description: description,
        View: perms.View,
        Edit: perms.Edit,
        Temporary: perms.Temporary,
        Delete: perms.Delete,
        ItemCreation: false,
        IsBeta: false,
        IsAdminGroup: false,
        WhiteListIPAddress: '*',
        ConfigValueSecurityGroups: menuItems
          .filter(i => selectedCardTypeIds.has(i.ConfigValueID))
          .map(i => ({ ConfigValueID: i.ConfigValueID, Name: i.Text, Icon: i.Icon })),
        SecurityGroupLayers: layersList
          .filter(l => selectedLayerIds.has(l.LayerId))
          .map(l => ({ LayerId: l.LayerId, Name: l.Name, Color: l.Color, TextColor: l.TextColor })),
        SecurityGroupProjects: projectsList
          .filter(p => selectedProjectIds.has(p.ProjectID))
          .map(p => ({ Name: p.Name })),
        PeopleSecurityGroup: peopleList
          .filter(p => selectedPersonIds.has(p.PersonID))
          .map(p => ({ PersonID: p.PersonID, Name: p.Name, PathImage: p.PathImage })),
        IsDeleted: false,
      });
    }
    catch
    {
      setError('Failed to create security group.');
    }
    finally
    {
      setSaving(false);
    }
  };

  const filteredProjects = projectsList.filter(p =>
    p.Name.toLowerCase().includes(projectSearch.toLowerCase()),
  );

  const filteredPeople = peopleList.filter(p =>
    p.Name.toLowerCase().includes(userSearch.toLowerCase()),
  );

  const stepTitle = {
    name: 'Add New Security Sub-Group',
    config: 'Add New Security Sub-Group',
    engagements: 'Add New Security Sub-Group',
    users: 'Add New Security Sub-Group',
  }[step];

  return createPortal(
    <div className="admin-console-portal fixed inset-0 z-[10003] flex items-center justify-center">
      <div className="fixed inset-0 bg-black/30" onClick={onClose} />
      <div className="relative z-10 bg-white rounded shadow-xl w-[650px] max-h-[80vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-5 pt-4 pb-3 border-b border-[#eee]">
          <h3 className="text-[16px] font-medium text-[#62aacb]">{stepTitle}</h3>
          <button onClick={onClose} className="text-[#bbb] hover:text-[#666]">
            <span className="material-icons text-[18px]">close</span>
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-5 py-4">
          {/* ── Step 1: Name ── */}
          {step === 'name' && (
            <div className="flex flex-col gap-4">
              <div className="flex flex-col gap-1">
                <label className="text-[12px] text-[#555]">Sub-Group Name:</label>
                <input
                  autoFocus
                  type="text"
                  value={name}
                  onChange={e => setName(e.target.value)}
                  onKeyDown={e =>
                  {
                    if (e.key === 'Enter') goNext();
                  }}
                  className="border border-[#ccc] rounded px-3 py-1.5 text-[13px] outline-none focus:border-[#62aacb]"
                />
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-[12px] text-[#555]">Description:</label>
                <input
                  type="text"
                  value={description}
                  onChange={e => setDescription(e.target.value)}
                  className="border border-[#ccc] rounded px-3 py-1.5 text-[13px] outline-none focus:border-[#62aacb]"
                />
              </div>
            </div>
          )}

          {/* ── Step 2: Config ── */}
          {step === 'config' &&
            (loadingConfig ? (
              <div className="flex items-center justify-center py-12">
                <span className="material-icons animate-spin text-[#62aacb] text-[32px]">
                  refresh
                </span>
              </div>
            ) : (
              <div className="grid grid-cols-3 gap-4">
                {/* Permissions */}
                <div>
                  <p className="text-[12px] font-semibold text-[#555] mb-2">Permissions</p>
                  <div className="flex flex-col gap-2">
                    {PERM_FIELDS.map(({ key, label }) => (
                      <label key={key} className="flex items-center gap-2 cursor-pointer">
                        <CustomCheckbox
                          checked={!!perms[key as keyof typeof perms]}
                          onChange={() =>
                            setPerms(p => ({ ...p, [key]: !p[key as keyof typeof perms] }))
                          }
                        />
                        <span className="text-[12px] text-[#333]">{label}</span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Card Types */}
                <div>
                  <p className="text-[12px] font-semibold text-[#555] mb-2">Item Types</p>
                  <div className="flex flex-col gap-2 max-h-[280px] overflow-y-auto">
                    {menuItems.map(item => (
                      <label
                        key={item.ConfigValueID}
                        className="flex items-center gap-2 cursor-pointer"
                      >
                        <CustomCheckbox
                          checked={selectedCardTypeIds.has(item.ConfigValueID)}
                          onChange={() =>
                            setSelectedCardTypeIds(prev =>
                            {
                              const next = new Set(prev);
                              next.has(item.ConfigValueID)
                                ? next.delete(item.ConfigValueID)
                                : next.add(item.ConfigValueID);
                              return next;
                            })
                          }
                        />
                        <i className={`${item.Icon} text-[13px] text-[#666]`} />
                        <span className="text-[12px] text-[#333]">{item.Text}</span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Layers */}
                <div>
                  <p className="text-[12px] font-semibold text-[#555] mb-2">Layers</p>
                  <div className="flex flex-col gap-2 max-h-[280px] overflow-y-auto">
                    {layersList.map(l => (
                      <label key={l.LayerId} className="flex items-center gap-2 cursor-pointer">
                        <CustomCheckbox
                          checked={selectedLayerIds.has(l.LayerId)}
                          onChange={() =>
                            setSelectedLayerIds(prev =>
                            {
                              const next = new Set(prev);
                              next.has(l.LayerId) ? next.delete(l.LayerId) : next.add(l.LayerId);
                              return next;
                            })
                          }
                        />
                        <div
                          className="w-[40px] h-[14px] rounded flex-shrink-0 border border-[#ddd]"
                          style={{ backgroundColor: l.Color === 'transparent' ? '#fff' : l.Color }}
                        />
                        <span className="text-[12px] text-[#333]">{l.Name}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            ))}

          {/* ── Step 3: Engagements ── */}
          {step === 'engagements' && (
            <div>
              <p className="text-[12px] text-[#555] mb-3">Select projects in this security group</p>
              <div className="flex items-center gap-2 mb-3">
                <span className="text-[12px] text-[#555]">Find by keyword:</span>
                <input
                  autoFocus
                  type="text"
                  value={projectSearch}
                  onChange={e => setProjectSearch(e.target.value)}
                  className="flex-1 border border-[#ccc] rounded px-2 py-1 text-[12px] outline-none focus:border-[#62aacb]"
                />
              </div>
              <div className="border border-[#ddd] rounded overflow-hidden">
                <div className="px-3 py-1.5 bg-[#f5f5f5] border-b border-[#ddd] text-[11px] font-semibold text-[#555]">
                  Name
                </div>
                <div className="max-h-[300px] overflow-y-auto">
                  {loadingProjects ? (
                    <div className="flex items-center justify-center py-8">
                      <span className="material-icons animate-spin text-[#62aacb] text-[24px]">
                        refresh
                      </span>
                    </div>
                  ) : filteredProjects.length === 0 ? (
                    <div className="text-[12px] text-[#aaa] text-center py-6 italic">
                      No projects found.
                    </div>
                  ) : (
                    filteredProjects.map((p, index) => (
                      <label
                        key={p.ProjectID + '-' + index}
                        className="flex items-center gap-3 px-3 py-2 border-b border-[#f0f0f0] hover:bg-[#f9f9f9] cursor-pointer"
                      >
                        <CustomCheckbox
                          checked={selectedProjectIds.has(p.ProjectID)}
                          onChange={() =>
                            setSelectedProjectIds(prev =>
                            {
                              const next = new Set(prev);
                              next.has(p.ProjectID)
                                ? next.delete(p.ProjectID)
                                : next.add(p.ProjectID);
                              return next;
                            })
                          }
                        />
                        <span className="text-[12px] text-[#333]">{p.Name}</span>
                      </label>
                    ))
                  )}
                </div>
              </div>
            </div>
          )}

          {/* ── Step 4: Users ── */}
          {step === 'users' && (
            <div>
              <p className="text-[12px] text-[#555] mb-3">Select users in this security group</p>
              <div className="flex items-center gap-2 mb-3">
                <span className="text-[12px] text-[#555]">Find by keyword:</span>
                <input
                  autoFocus
                  type="text"
                  value={userSearch}
                  onChange={e => setUserSearch(e.target.value)}
                  className="flex-1 border border-[#ccc] rounded px-2 py-1 text-[12px] outline-none focus:border-[#62aacb]"
                />
              </div>
              <div className="border border-[#ddd] rounded overflow-hidden">
                <div className="px-3 py-1.5 bg-[#f5f5f5] border-b border-[#ddd] text-[11px] font-semibold text-[#555]">
                  Name
                </div>
                <div className="max-h-[300px] overflow-y-auto">
                  {loadingPeople ? (
                    <div className="flex items-center justify-center py-8">
                      <span className="material-icons animate-spin text-[#62aacb] text-[24px]">
                        refresh
                      </span>
                    </div>
                  ) : filteredPeople.length === 0 ? (
                    <div className="text-[12px] text-[#aaa] text-center py-6 italic">
                      No users found.
                    </div>
                  ) : (
                    filteredPeople.map((p, index) => (
                      <label
                        key={p.PersonID + '-' + index}
                        className="flex items-center gap-3 px-3 py-2 border-b border-[#f0f0f0] hover:bg-[#f9f9f9] cursor-pointer"
                      >
                        <CustomCheckbox
                          checked={selectedPersonIds.has(p.PersonID)}
                          onChange={() =>
                            setSelectedPersonIds(prev =>
                            {
                              const next = new Set(prev);
                              next.has(p.PersonID) ? next.delete(p.PersonID) : next.add(p.PersonID);
                              return next;
                            })
                          }
                        />
                        {p.PathImage ? (
                          <div
                            className="w-[22px] h-[22px] rounded-sm bg-cover bg-center border border-[#e0e0e0] flex-shrink-0"
                            style={{ backgroundImage: `url(${p.PathImage})` }}
                          />
                        ) : (
                          <div className="w-[22px] h-[22px] rounded-sm bg-[#ddd] flex items-center justify-center border border-[#e0e0e0] flex-shrink-0">
                            <span className="material-icons text-[12px] text-[#999]">person</span>
                          </div>
                        )}
                        <span className="text-[12px] text-[#333]">{p.Name}</span>
                      </label>
                    ))
                  )}
                </div>
              </div>
            </div>
          )}

          {error && <p className="text-[11px] text-[#e57373] mt-3">{error}</p>}
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t border-[#eee] flex items-center justify-end gap-2">
          {step !== 'name' && (
            <button
              onClick={goBack}
              disabled={saving}
              className="px-4 py-1.5 text-[12px] bg-[#999] text-white rounded hover:bg-[#777] disabled:opacity-50 flex items-center gap-1"
            >
              <span className="material-icons text-[14px]">chevron_left</span>
              Back
            </button>
          )}
          {step !== 'users' ? (
            <button
              onClick={goNext}
              className="px-4 py-1.5 text-[12px] bg-[#62aacb] text-white rounded hover:bg-[#4e99ba] flex items-center gap-1"
            >
              Next
              <span className="material-icons text-[14px]">chevron_right</span>
            </button>
          ) : (
            <button
              onClick={handleSave}
              disabled={saving}
              className="px-4 py-1.5 text-[12px] bg-[#62aacb] text-white rounded hover:bg-[#4e99ba] disabled:opacity-50"
            >
              {saving ? 'Saving...' : 'Save'}
            </button>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
};
